import { CORE_CONSTANT } from '@salescore/core'
import { Array, Record, Result } from 'effect'
import { isNotNullish, isNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { z } from 'zod'

import {
  DATA_EXTENSION_LICENSES,
  type DataExtensionLicense,
  ORGANIZATION_LICENSES,
  ORGANIZATION_PLANS,
  type OrganizationLicense,
  type OrganizationPlan,
} from './constant.ts'

/** クライアントから受け取る license 名 */
export const organizationLicenseSchema = z.enum(ORGANIZATION_LICENSES)

/** 組織ライセンスの利用可否を判定する対象のユーザー */
export interface OrganizationLicenseUser {
  role: string
  organization: {
    organizationPlans: ReadonlyArray<{ license: string }>
  }
}

/**
 * 組織ライセンスを利用できるかを判定する。
 * plan (free / standard) は契約形態の記録であり、判定には使わない。
 * ロール権限とは AND で併用する判定であり、ロール権限を置き換えない。
 */
export const canUseOrganizationLicense = (user: OrganizationLicenseUser, license: OrganizationLicense): boolean =>
  user.role === 'admin' || user.organization.organizationPlans.some((x) => x.license === license)

/** カスタムビューのモデル */
export const isCustomViewModel = (model: { name: string }): boolean =>
  model.name.startsWith(CORE_CONSTANT.CUSTOM_MODEL_PREFIX)

/** カスタムオブジェクトのモデル */
export const isCustomObjectModel = (model: { name: string }): boolean =>
  model.name.startsWith(CORE_CONSTANT.SALESCORE_CUSTOM_MODEL_PREFIX)

/** スナップショットのモデル */
export const isSnapshotModel = (model: { name: string }): boolean =>
  model.name.startsWith(CORE_CONSTANT.SNAPSHOT_MODEL_PREFIX)

/** 顧客が SALESCORE 上で定義したモデル。カスタムビューとカスタムオブジェクトの両方が該当する */
export const isCustomModel = (model: { name: string }): boolean =>
  isCustomViewModel(model) || isCustomObjectModel(model)

/**
 * モデルの利用に必要な組織ライセンスを返す。連携先由来の通常モデルは組織ライセンスを必要としないので null。
 *
 * スナップショット・カスタムオブジェクト・カスタムビューはいずれも SALESCORE が生成するモデルで、
 * それぞれ別の組織ライセンスで利用可否を決める。
 */
const getModelOrganizationLicense = (model: { name: string }): OrganizationLicense | null => {
  if (isSnapshotModel(model)) {
    return ORGANIZATION_LICENSES.snapshot
  }
  if (isCustomObjectModel(model)) {
    return ORGANIZATION_LICENSES.customObject
  }
  if (isCustomViewModel(model)) {
    return ORGANIZATION_LICENSES.customView
  }
  return null
}

/**
 * モデルを利用できるかを判定する。組織ライセンスを必要としないモデルは常に利用できる。
 * 必要とするモデルは canUseOrganizationLicense と同じ規則で判定する。
 */
export const canUseOrganizationLicenseForModel = (user: OrganizationLicenseUser, modelName: string): boolean => {
  const license = getModelOrganizationLicense({ name: modelName })
  return isNullish(license) || canUseOrganizationLicense(user, license)
}

/**
 * 顧客向けの画面に出す DataExtension のサブサービス名を返す。license の識別子は顧客に見せない。
 * サブサービスを追加したら、この switch が網羅でなくなって tsc が落ちるので表示名の追加漏れに気付ける。
 */
export const getDataExtensionLicenseLabel = (license: DataExtensionLicense): string => {
  switch (license) {
    case DATA_EXTENSION_LICENSES.snapshot: {
      return t(`スナップショット`)
    }
    case DATA_EXTENSION_LICENSES.customObject: {
      return t(`カスタムオブジェクト`)
    }
    case DATA_EXTENSION_LICENSES.customColumn: {
      return t(`カスタムカラム`)
    }
  }
}

/** DataExtension のサブサービスの license 名。organization_plan の license 列との突合に使う */
export const DATA_EXTENSION_LICENSE_NAMES: readonly string[] = Record.values(DATA_EXTENSION_LICENSES)

/**
 * 組織ライセンスの行から、DataExtension 全体として扱う plan を 1 つ決める。
 * サブサービスに有料が 1 つでもあれば有料、無料だけなら無料、1 つも付与されていなければ null を返す。
 * 有料を優先するのは、plan が食い違う異常データで契約の重い側を見落とさないため。
 *
 * この値は organization_plan に保存しない。管理画面のライセンスカード・ライセンスタグ・組織一覧 CSV は
 * すべてサブサービスの付与状況から毎回導出する。
 */
export const getDataExtensionPlan = (
  organizationPlans: ReadonlyArray<{ license: string; plan: OrganizationPlan | null }>,
): OrganizationPlan | null => {
  const grantedPlans = Array.filterMap(organizationPlans, ({ license, plan }) =>
    isNotNullish(plan) && Array.contains(DATA_EXTENSION_LICENSE_NAMES, license)
      ? Result.succeed(plan)
      : Result.failVoid,
  )
  if (Array.isArrayEmpty(grantedPlans)) {
    return null
  }
  return Array.contains(grantedPlans, ORGANIZATION_PLANS.standard)
    ? ORGANIZATION_PLANS.standard
    : ORGANIZATION_PLANS.free
}
